/**
 * Sticky Audit Announcement Bar
 * ------------------------------
 * Full-width bar pinned to the very top of every landing page.
 * - Click the bar → fires a `rekisa:open-audit` window event that AuditPopup
 *   listens for and force-opens the popup (even if it was previously dismissed).
 * - × dismisses the bar with a 24-hour cooldown (localStorage).
 * - Publishes its height to `--rekisa-announce-h` so the fixed <Header /> can
 *   read it and shift itself down while the bar is visible.
 * - Per-service copy + accent colour, matching the popup styling.
 */
import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/tracking";

const COPY = {
  paid_ads: {
    accent: "#1a73e8",
    text: "🎯 Free Paid-Ads Audit — 3 leaks costing you sales, sent as a Loom in 48 hours.",
    cta: "Claim yours",
  },
  whatsapp_automation: {
    accent: "#128c4b",
    text: "💬 Free WhatsApp Audit — see the 3 leaks costing you sales, reviewed by Thato personally.",
    cta: "Claim yours",
  },
  website_app_creation: {
    accent: "#0B1120",
    text: "⚠️ Your website may be generating enquiries that nobody follows up.",
    cta: "Get a Free Lead-Loss Audit",
  },
};

const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const KEY = (svc) => `rekisa_announce_dismissed_${svc}`;
const CSS_VAR = "--rekisa-announce-h";

function shouldShow(service) {
  try {
    const raw = localStorage.getItem(KEY(service));
    if (!raw) return true;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return true;
    if (Date.now() - ts > DISMISS_TTL_MS) {
      localStorage.removeItem(KEY(service));
      return true;
    }
    return false;
  } catch {
    return true;
  }
}

export default function AuditAnnouncement({ service }) {
  const copy = COPY[service];
  const [visible, setVisible] = useState(false);

  // Init visibility from localStorage on mount
  useEffect(() => {
    setVisible(shouldShow(service));
  }, [service]);

  // Publish bar height to CSS variable so the fixed <Header /> can offset itself
  useEffect(() => {
    const root = document.documentElement;
    if (visible) {
      // Bar is ~40px on desktop; header will pick this up via var(--rekisa-announce-h)
      root.style.setProperty(CSS_VAR, "40px");
    } else {
      root.style.removeProperty(CSS_VAR);
    }
    return () => root.style.removeProperty(CSS_VAR);
  }, [visible]);

  const openPopup = (source) => {
    trackEvent("audit_announcement_click", { service, source });
    // Broadcast — the AuditPopup on this page listens and force-opens itself
    window.dispatchEvent(new CustomEvent("rekisa:open-audit"));
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(KEY(service), String(Date.now()));
    } catch {
      /* ignore quota */
    }
    trackEvent("audit_announcement_dismiss", { service });
    setVisible(false);
  };

  if (!copy || !visible) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => openPopup("bar_click")}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPopup("bar_keyboard");
        }
      }}
      className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-2 pl-3 pr-10 py-2 text-white cursor-pointer select-none group"
      style={{ background: copy.accent, height: "40px" }}
      data-testid={`audit-announcement-${service}`}
    >
      <div className="flex items-center gap-2 min-w-0 max-w-full">
        <span className="text-[13px] sm:text-sm font-medium truncate">{copy.text}</span>
        <span
          className="hidden sm:inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest bg-white/20 group-hover:bg-white/30 rounded-full px-2.5 py-0.5 transition-colors shrink-0"
          data-testid={`audit-announcement-cta-${service}`}
        >
          {copy.cta} →
        </span>
      </div>
      <button
        onClick={dismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full hover:bg-white/20 text-white/80 hover:text-white text-lg leading-none"
        aria-label="Dismiss announcement"
        data-testid={`audit-announcement-close-${service}`}
      >
        ×
      </button>
    </div>
  );
}
